import { Browser } from '@capacitor/browser';
import { IonCol, IonGrid, IonIcon, IonRow } from '@ionic/react';
import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import {
  trainingLink,
  yourAccountLink,
  jettingCalculatorLink,
  productInformationLink,
  ResourceLink,
  feedbackLink,
  contactLink,
} from 'src/constants';
import NavCard from '../nav-card/NavCard';
import './LinkCardList.scss';
import { useSocket } from 'src/utils/socket';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import SupportCard from '../experience-card/SupportCard';
import FeedbackModal from '../profile/FeedbackModal';
import { NavCardMessages } from 'src/intl/NavCardMessages';
import { useToast } from '../reusable-components/useToast';

const stoneAgeResourceLinks: ResourceLink[] = [
  yourAccountLink,
  jettingCalculatorLink,
  productInformationLink,
  trainingLink,
];

const LinkCardList: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const [present] = useToast();
  const { request: sentinelRequest } = useSocket(SocketContextSentinel, 'faqs');
  const [feedbackModalOpen, setFeedbackModalOpen] = useState<boolean>(false);

  const supportListRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => supportListRef.current?.scrollIntoView();

  const submitFeedback = useCallback(
    async (feedbackText: string | undefined, canContact: boolean) => {
      setFeedbackModalOpen(false);
      scrollToTop();
      try {
        await sentinelRequest('feedback sendFeedback', { feedback: feedbackText, canContact });
        await present(
          {
            message: intl.formatMessage(NavCardMessages.experienceFeedbackSuccess),
          },
          'success',
        );
      } catch (e) {
        console.log('error sending message', e);
      }
    },
    [intl, present, sentinelRequest],
  );

  const clickCard = async (url?: string) => {
    if (url) {
      await Browser.open({ url, presentationStyle: 'popover', windowName: '_blank' });
    } else {
      history.push('/');
    }
  };

  return (
    <div ref={supportListRef}>
      <IonGrid className="link-list-grid">
        <IonRow>
          {stoneAgeResourceLinks.map((link, index) => (
            <IonCol size="6" sizeLg="3" key={index}>
              <div
                onClick={() => clickCard(link.actionUrl)}
                className="link-card-container"
                data-testid={`stoneAgeResources-${link.automationId}`}
              >
                <NavCard
                  title={intl.formatMessage(link.title)}
                  subtitle={intl.formatMessage(link.subtitle)}
                  icon={<IonIcon icon={link.icon} color="primary" />}
                />
              </div>
            </IonCol>
          ))}
        </IonRow>
        <IonRow>
          <IonCol size="12">
            <SupportCard
              buttonText={intl.formatMessage(feedbackLink.buttonText)}
              title={intl.formatMessage(feedbackLink.title)}
              subtitle={intl.formatMessage(feedbackLink.subtitle)}
              onClick={() => setFeedbackModalOpen(true)}
            />
          </IonCol>
          <IonCol size="12">
            <SupportCard
              buttonText={intl.formatMessage(contactLink.buttonText)}
              title={intl.formatMessage(contactLink.title)}
              subtitle={intl.formatMessage(contactLink.subtitle)}
              onClick={() => clickCard(contactLink.actionUrl)}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        onSave={(e, canContact) => submitFeedback(e, canContact)}
      />
    </div>
  );
};

export default LinkCardList;
